/**
 * NIRSALCarbonStructuring — Carbon Asset Assembly Layer
 * Section 1: Status Summary  Section 2: Pathways  Section 3: CPA Grouping
 * Section 4: PoA             Section 5: Portfolio Aggregation
 */
import React, { useState, useMemo } from "react";
import { useNIRSAL } from "@/context/NIRSALContext";
import { PageHeader, SectionCard } from "@/components/ds";
import { ChevronDown, ChevronUp } from "lucide-react";

const fmtN = (v, u="") => { if (!v&&v!==0) return "—"; const n=Number(v); if (n>=1e6) return `${(n/1e6).toFixed(1)}M ${u}`.trim(); if (n>=1000) return `${(n/1000).toFixed(0)}K ${u}`.trim(); return `${n} ${u}`.trim(); };
const fmtU = (v) => { if (!v) return "—"; const n=Number(v); if (n>=1e6) return `$${(n/1e6).toFixed(1)}M`; if (n>=1000) return `$${(n/1000).toFixed(0)}K`; return `$${n}`; };

const CPA_GROUPS = [
  { id:"CPA-BIODIGESTER", label:"Biodigester Clusters", color:"#1e3a5f", bg:"#eff6ff",
    desc:"Methane avoidance from dairy, poultry & agri-processing effluents", methodology:"AMS-III.D / AMS-III.R",
    technologies:["biodigester_manure","rice_wastewater_methane","biodigester_agri"], pathway:"Article 6.4" },
  { id:"CPA-SOLAR", label:"Solar Energy Clusters", color:"#d97706", bg:"#fffbeb",
    desc:"Renewable energy displacement via solar irrigation and cold storage", methodology:"AMS-I.A / ACM0002",
    technologies:["solar_irrigation","solar_cold_room","solar_offgrid"], pathway:"VCM" },
  { id:"CPA-COMPOSTING", label:"Composting & Briquette Clusters", color:"#059669", bg:"#ecfdf5",
    desc:"Agri-residue composting and biomass briquettes", methodology:"AMS-III.E / AMS-I.C",
    technologies:["composting_agri_residues","biomass_briquettes","agroforestry_trees"], pathway:"VCM" },
  { id:"CPA-PLASTIC", label:"Plastic Recovery Hubs", color:"#7c3aed", bg:"#f5f3ff",
    desc:"Plastic collection, sorting, and recycling", methodology:"Verra Plastic Waste Reduction Standard",
    technologies:["plastic_collection_recycling"], pathway:"Plastic Credits" },
];

export default function NIRSALCarbonStructuring() {
  const { vbCache, selectedPeriod, loading, consolidated } = useNIRSAL();
  const [expandedCPA, setExpandedCPA] = useState(null);
  const vb   = vbCache[selectedPeriod] || {};
  const opps = vb.opportunities || [];
  const portfolio = vb.portfolio || consolidated?.portfolio || {};

  const cpaGroups = useMemo(() => CPA_GROUPS.map(g => ({
    ...g,
    projects: opps.filter(o => g.technologies.includes(o.technology)),
    totalRed: opps.filter(o => g.technologies.includes(o.technology)).reduce((s,o)=>s+(o.reduction_tco2e_yr||0),0),
  })).filter(g => g.projects.length > 0), [opps]);

  const art6Count = (portfolio.art6_4_clusters || 0);
  const totalRed  = portfolio.total_reduction_tco2e_yr || 0;
  const readiness = consolidated?.readiness_score || 0;

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading structuring…</div>;

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-6">
      <PageHeader label="CARBON STRUCTURING" title="" />
      <p className="text-xs text-slate-400 -mt-4">Carbon Asset Assembly Layer · PoA + CPA architecture · Article 6.4 & VCM pathway structuring</p>

      {/* Section 1: Status Summary */}
      <div className="bg-[#0f2d5c] rounded-xl p-5 text-white">
        <p className="text-xs font-bold opacity-60 uppercase tracking-wider mb-3">Structuring Status Summary</p>
        <div className="grid grid-cols-4 gap-4">
          {[
            ["Program Status", "Active — Pilot Phase"],
            ["Primary Pathway", "Article 6.4 (PACM)"],
            ["Secondary Pathway", "VCM + Plastic Credits"],
            ["Next Critical Action", "NCCC pre-application — Phase 2"],
          ].map(([k,v]) => (
            <div key={k}><p className="text-[10px] opacity-50">{k}</p><p className="text-sm font-bold mt-0.5">{v}</p></div>
          ))}
        </div>
        <div className="flex gap-4 mt-4 pt-4 border-t border-white/10 flex-wrap">
          {[
            [art6Count + " Art.6.4 eligible", "bg-blue-800/40"],
            [fmtN(totalRed,"tCO₂e/yr") + " portfolio potential", "bg-emerald-800/40"],
            [cpaGroups.length + " CPA groups active", "bg-purple-800/40"],
            ["1 National PoA", "bg-amber-800/40"],
          ].map(([t,c]) => (
            <span key={t} className={`text-xs px-3 py-1 rounded-full font-medium ${c}`}>{t}</span>
          ))}
        </div>
      </div>

      {/* Section 2: Carbon Pathways */}
      <SectionCard title="Section 2 — Carbon Pathways">
        <div className="p-4 grid grid-cols-3 gap-4">
          {[
            { title: "A. Article 6.4 — ITMO Pathway", status: "Primary — Programme Architecture", color: "#1e3a5f", bg:"#eff6ff",
              rows: [["Authorising body","NCCC — Nigeria Climate Change Council"],["Mechanism","PACM supervisory body"],["ITMO potential","Country-to-country transfer of mitigation outcomes"],["Methodology basis","AMS-III.D / AMS-III.R (biodigester) — IPCC 2006 FOD model"],["Price range","$10–$40/tCO₂e (bilateral)"],["Timeline","24–36 months (NCCC engagement)"],["Eligible clusters",art6Count + " clusters / " + fmtN(portfolio.art6_4_reduction,"tCO₂e/yr")]] },
            { title: "B. VCM — Voluntary Carbon Market", status: "Recommended for non-Art.6.4 clusters", color: "#059669", bg:"#ecfdf5",
              rows: [["Standards","Verra VCS · Gold Standard · ACM / AMS"],["Typical use","Biodigesters, solar, composting"],["Methodologies","AMS-III.D/R (biodigester) · AMS-I.A (solar) · AMS-III.E (composting)"],["Price range","$5–$20/tCO₂e (VCM market)"],["Timeline","18–24 months"],["Integrity","High — VVB validation required"],["Eligible clusters",(portfolio.vcm_clusters||0) + " clusters"]] },
            { title: "C. Plastic Credits", status: "Applicable — Lagos & agri-waste hubs", color: "#7c3aed", bg:"#f5f3ff",
              rows: [["Standard","Verra Plastic Waste Reduction Standard"],["Mechanism","Plastic credit issuance per tonne recovered"],["Price","~$150/tonne plastic recovered"],["Eligible clusters",(portfolio.plastic_clusters||0) + " clusters"],["Plastic recovered",fmtN(portfolio.total_plastic_recovered_t_yr,"t/yr")]] },
          ].map(({ title, status, color, bg, rows }) => (
            <div key={title} className="rounded-lg p-4 border" style={{ background: bg, borderColor: color + "33" }}>
              <p className="text-xs font-black mb-1" style={{ color }}>{title}</p>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-bold text-white mb-3 inline-block" style={{ background: color }}>{status}</span>
              {rows.map(([k,v]) => (
                <div key={k} className="flex justify-between text-xs py-1 border-b border-black/5">
                  <span className="text-slate-500">{k}</span><span className="font-semibold" style={{color}}>{v}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Section 3: CPA Grouping */}
      <SectionCard title="Section 3 — CPA Grouping">
        <div className="p-4 space-y-3">
          {cpaGroups.map(g => (
            <div key={g.id} className="border rounded-xl overflow-hidden" style={{ borderColor: g.color + "33" }}>
              <button className="w-full flex items-center justify-between p-4" style={{ background: g.bg }}
                onClick={() => setExpandedCPA(e => e === g.id ? null : g.id)}>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ background: g.color }} />
                  <div className="text-left">
                    <p className="text-xs font-bold" style={{ color: g.color }}>{g.label}</p>
                    <p className="text-[10px] text-slate-500">{g.projects.length} clusters · {fmtN(g.totalRed,"tCO₂e/yr")} · {g.pathway}</p>
                  </div>
                </div>
                {expandedCPA === g.id ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
              </button>
              {expandedCPA === g.id && (
                <div className="p-4 bg-white">
                  <p className="text-xs text-slate-500 mb-3">{g.desc} · Methodology: {g.methodology}</p>
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-slate-100">
                      <th className="text-left py-1.5 px-2 text-slate-400 font-medium">Cluster</th>
                      <th className="text-left py-1.5 px-2 text-slate-400 font-medium">State</th>
                      <th className="text-right py-1.5 px-2 text-slate-400 font-medium">Reduction</th>
                      <th className="text-left py-1.5 px-2 text-slate-400 font-medium">Evidence</th>
                      <th className="text-left py-1.5 px-2 text-slate-400 font-medium">Status</th>
                    </tr></thead>
                    <tbody>
                      {g.projects.map(o => (
                        <tr key={o.opp_id} className="border-b border-slate-50">
                          <td className="py-1.5 px-2 font-medium truncate max-w-[150px]">{o.name}</td>
                          <td className="py-1.5 px-2 text-slate-500">{o.state}</td>
                          <td className="py-1.5 px-2 text-right text-emerald-700 font-bold">{fmtN(o.reduction_tco2e_yr,"tCO₂e/yr")}</td>
                          <td className="py-1.5 px-2 capitalize text-slate-500">{o.evidence_level}</td>
                          <td className="py-1.5 px-2 capitalize text-slate-500">{o.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Section 4: PoA */}
      <SectionCard title="Section 4 — Programme of Activities (PoA)">
        <div className="p-4">
          <div className="bg-[#0f2d5c]/5 border border-[#0f2d5c]/20 rounded-xl p-4 mb-4">
            <p className="text-xs font-black text-[#1e3a5f] mb-1">NIRSAL National PoA — Climate-Smart Agriculture Nigeria</p>
            <p className="text-xs text-slate-500">Programme architecture: 1 national PoA · CPAs nested by state / commodity / technology</p>
            <div className="grid grid-cols-4 gap-3 mt-3">
              {[
                ["CPAs in PoA", cpaGroups.length],
                ["Total Clusters", opps.length],
                ["PoA-eligible", opps.filter(o=>o.art6_eligible||o.vcm_eligible).length],
                ["Readiness Score", readiness + "/100"],
              ].map(([k,v]) => (
                <div key={k} className="text-center"><p className="text-xs text-slate-400">{k}</p><p className="text-lg font-black text-[#1e3a5f]">{v}</p></div>
              ))}
            </div>
          </div>
          {[
            ["NCCC Authorization", "Host country approval required for Art.6.4 ITMOs", "Pending — Phase 2"],
            ["DOE / VVB Engagement", "Accredited Designated Operational Entity pre-engagement", "Pending — Phase 2"],
            ["Corresponding Adjustment", "Required for Art.6.4 transfers — NCCC commitment needed", "Not Initiated"],
            ["DMRV Framework", "Digital MRV plan aligned with PACM requirements", "In Progress"],
            ["Registry Alignment", "Verra VCS (VCM) + PACM registry (Art.6.4)", "Design Phase"],
          ].map(([k,v,s]) => (
            <div key={k} className="flex items-center justify-between py-2 border-b border-slate-50 text-xs">
              <div><p className="font-medium text-slate-700">{k}</p><p className="text-slate-400 text-[10px]">{v}</p></div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${s.includes("Phase") || s.includes("Design") || s.includes("Progress") ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{s}</span>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Section 5: Portfolio Aggregation */}
      <SectionCard title="Section 5 — Portfolio Aggregation">
        <div className="p-4">
          <div className="grid grid-cols-3 gap-4 mb-4">
            {[
              ["Total Eligible Projects", opps.filter(o=>o.art6_eligible||o.vcm_eligible).length, "Art.6.4 + VCM + Plastic"],
              ["Total Eligible tCO₂e", fmtN(totalRed,"tCO₂e/yr"), "Portfolio reduction potential"],
              ["Portfolio Readiness", readiness + "/100", "DMRV-backed score"],
            ].map(([k,v,s]) => (
              <div key={k} className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-400">{k}</p>
                <p className="text-xl font-black text-[#1e3a5f]">{v}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{s}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              ["Art.6.4", portfolio.art6_4_reduction, "$10/t","$40/t", "#1e3a5f"],
              ["VCM", portfolio.vcm_reduction, "$5/t","$20/t", "#059669"],
              ["Plastic Credits", portfolio.total_plastic_recovered_t_yr ? portfolio.plastic_value_usd : null, "$100/t","$200/t", "#7c3aed"],
            ].map(([l,red,lo,hi,c]) => (
              <div key={l} className="rounded-lg p-3 border" style={{ borderColor: c+"33" }}>
                <p className="text-xs font-bold mb-1" style={{color:c}}>{l}</p>
                <p className="text-sm font-black text-slate-800">{red ? fmtN(red, red === portfolio.plastic_value_usd ? "USD/yr" : "tCO₂e/yr") : "—"}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{lo} – {hi} indicative range</p>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
